import { Box, Typography, Button, TextField, Stack, Alert, Link } from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { authService } from '@/services/authService';

const schema = yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setLoading(true);
      setError(null);
      await authService.requestPasswordReset(data.email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Error sending recovery email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        '@media (max-width: 768px)': {
          flexDirection: 'column',
        },
      }}
    >
      {/* Left Side - Brand Section */}
      <Box
        sx={{
          flex: 3,
          background: 'linear-gradient(135deg, #3b4d66 0%, #2c3e50 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: { xs: 4, md: 8 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ position: 'relative', zIndex: 1, maxWidth: '500px' }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#4A90E2',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                }}
              >
                AH
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              Alley Hooper
            </Typography>
          </Box>

          {/* Main heading */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              mb: 3,
              background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {success ? 'Check Your Email' : 'Need Help?'}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '400px',
            }}
          >
            {success
              ? "We've sent password recovery instructions to your email. Please check your inbox and follow the instructions."
              : "Enter your email address and we'll send you instructions to reset your password."}
          </Typography>
        </motion.div>
      </Box>

      {/* Right Side - Form Section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 4, md: 8 },
          backgroundColor: 'white',
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ width: '100%', maxWidth: '400px' }}
        >
          {success ? (
            // Success State
            <Stack spacing={4} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                  }}
                >
                  ✓
                </Typography>
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A1A',
                  fontSize: { xs: '1.8rem', md: '2rem' },
                }}
              >
                Email Sent!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                }}
              >
                We sent password recovery instructions to{' '}
                <strong>{getValues('email')}</strong>. Check your inbox and follow the
                instructions.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#999',
                }}
              >
                Didn't find the email? Check your spam folder.
              </Typography>

              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(31, 41, 55, 0.2)',
                  '&:hover': {
                    backgroundColor: '#111827',
                    boxShadow: '0 6px 16px rgba(31, 41, 55, 0.3)',
                  },
                }}
              >
                Back to Login
              </Button>
            </Stack>
          ) : (
            // Form State
            <>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#1a1a1a',
                    mb: 1,
                    fontSize: { xs: '1.8rem', md: '2rem' },
                  }}
                >
                  Forgot Password?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Remember it?{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{
                      color: '#4A90E2',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: '8px',
                    }}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: '#374151',
                        fontWeight: 500,
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      {...register('email')}
                      type="email"
                      fullWidth
                      placeholder="Enter your email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #d1d5db',
                          '&:hover': {
                            borderColor: '#9ca3af',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            borderColor: '#4A90E2',
                          },
                          '& fieldset': {
                            border: 'none',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#374151',
                          fontSize: '1rem',
                        },
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      backgroundColor: '#1f2937',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(31, 41, 55, 0.2)',
                      '&:hover': {
                        backgroundColor: '#111827',
                        boxShadow: '0 6px 16px rgba(31, 41, 55, 0.3)',
                      },
                      '&:disabled': {
                        backgroundColor: '#e5e7eb',
                        color: '#9ca3af',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Instructions'}
                  </Button>
                </Stack>
              </form>
            </>
          )}
        </motion.div>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
